import { useState, useEffect } from "react";
import { CalendarDays, Clock, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const DEFAULT_SLOTS = ["09:00", "10:00", "11:00", "12:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

export const WeeklySchedule = () => {
  const [schedule, setSchedule] = useState([]);
  const [active, setActive] = useState(0);
  const [closed, setClosed] = useState(false);
  const [slots, setSlots] = useState(DEFAULT_SLOTS);
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/weekly-schedule").then((r) => setSchedule(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const d = schedule.find((x) => x.weekday === active);
    if (d) {
      setClosed(!!d.closed);
      setSlots(d.slots?.length ? d.slots : DEFAULT_SLOTS);
    }
  }, [active, schedule]);

  const toggleSlot = (s) => setSlots((l) => (l.includes(s) ? l.filter((x) => x !== s) : [...l, s].sort()));

  const addCustom = () => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(custom)) { toast.error("Formato orario non valido (usa HH:MM, es. 14:30)"); return; }
    if (slots.includes(custom)) { toast.error("Fascia già presente"); return; }
    setSlots((l) => [...l, custom].sort());
    setCustom("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/weekly-schedule/${active}`, { closed, slots });
      toast.success(`Orari del ${DAYS[active]} salvati`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const allSlots = [...new Set([...DEFAULT_SLOTS, ...slots])].sort();

  return (
    <div className="bg-[#10141D] border border-[#222A3B] rounded-2xl p-6 space-y-6">
      <div>
        <h3 className="font-heading font-semibold text-white text-lg flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-sky-400" /> Orari settimanali standard
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Valgono ogni settimana per quel giorno. Le eccezioni per singola data (sotto) hanno sempre la priorità.
        </p>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {DAYS.map((d, i) => {
          const cfg = schedule.find((x) => x.weekday === i);
          const isClosed = cfg?.closed;
          return (
            <button key={d} type="button" data-testid={`weekly-day-${i}`} onClick={() => setActive(i)}
              className={`py-2 px-1 rounded-xl text-xs font-semibold border transition-all duration-200 ${
                active === i
                  ? "bg-sky-400 text-[#050B14] border-sky-400 shadow-[0_0_15px_rgba(56,189,248,0.25)]"
                  : isClosed
                  ? "bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
                  : "bg-[#08090C] border-[#222A3B] text-slate-300 hover:border-sky-500/40"}`}>
              {d.slice(0, 3)}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between bg-[#08090C] border border-[#222A3B] rounded-xl p-4">
        <div>
          <p className="text-sm font-medium text-white">{DAYS[active]} chiuso</p>
          <p className="text-xs text-slate-500">Nessuna prenotazione possibile di {DAYS[active].toLowerCase()}</p>
        </div>
        <Switch data-testid="weekly-closed-switch" checked={closed} onCheckedChange={setClosed} />
      </div>
      {!closed && (
        <div>
          <Label className="text-slate-300 flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky-400" /> Fasce orarie attive di {DAYS[active].toLowerCase()}
          </Label>
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {allSlots.map((s) => {
              const on = slots.includes(s);
              return (
                <button key={s} type="button" data-testid={`weekly-slot-${s.replace(":", "")}`} onClick={() => toggleSlot(s)}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                    on
                      ? "bg-sky-400 text-[#050B14] border-sky-400 shadow-[0_0_15px_rgba(56,189,248,0.25)]"
                      : "bg-[#08090C] border-[#222A3B] text-slate-500 hover:border-sky-500/40 hover:text-slate-300"}`}>
                  {s}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex gap-2">
            <Input data-testid="weekly-custom-slot-input" placeholder="Nuova fascia, es. 14:30" value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
            <Button type="button" data-testid="weekly-add-slot-btn" onClick={addCustom} variant="outline"
              className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10 bg-transparent shrink-0">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </div>
        </div>
      )}
      <Button data-testid="weekly-save-btn" onClick={save} disabled={saving}
        className="bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold h-11">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salva orari del {DAYS[active]}</>}
      </Button>
    </div>
  );
};
